import { useState, useEffect } from 'react';

const Publicaciones = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [publicaciones, setPublicaciones] = useState([]);

  useEffect(() => {
    const cargarPublicaciones = async () => {
      try {
        const res = await fetch('https://logia-production-725b.up.railway.app/api/publicaciones');
        const data = await res.json();
        if (data.success) {
          setPublicaciones(data.data);
        }
      } catch (error) {
        console.error("Error cargando publicaciones:", error);
      }
    };
    cargarPublicaciones();
    window.scrollTo(0, 0);
  }, []);

  // Formateador para crear strings del tipo "Octubre 2026"
  const formatearMes = (fecha) => {
    const fechaFormat = new Date(fecha);
    return fechaFormat.toLocaleDateString('es-PE', { month: 'long', year: 'numeric' });
  };

  const publicacionesFiltradas = publicaciones.filter(pub => 
    pub.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pub.descripcion_corta && pub.descripcion_corta.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#C5A059] selection:text-[#050505] font-sans pt-24 flex flex-col">
      
      {/* Hero Section */}
      <section className="relative w-full pt-32 pb-16 md:pt-40 md:pb-24 flex flex-col items-center justify-center overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1589829085413-56de8ae18c73?q=80')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/90 via-[#050505]/60 to-[#050505]"></div>
        <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
          <p className="text-[#C5A059] tracking-[0.4em] uppercase text-xs md:text-sm font-light mb-4 animate-fade-in">Biblioteca Digital</p>
          <h1 className="font-serif text-4xl md:text-6xl font-normal text-white tracking-tight animate-fade-in [animation-delay:0.3s] drop-shadow-lg mb-6">
            Publicaciones
          </h1>
          <p className="text-gray-400 font-light text-sm md:text-base max-w-2xl mx-auto animate-fade-in [animation-delay:0.6s]">
            Acceda al archivo de nuestras revistas mensuales. Documentos históricos, noticias jurisdiccionales y artículos de instrucción masónica.
          </p>
        </div>
      </section>

      {/* Contenido Principal */}
      <section className="relative z-20 max-w-[90rem] mx-auto px-6 lg:px-12 py-16 md:py-24 flex-grow w-full">
        
        {/* Panel de Búsqueda */}
        <div className="bg-gradient-to-br from-[#0a0a0a] to-[#111111] border border-white/5 p-6 md:p-8 mb-12 shadow-2xl flex flex-col md:flex-row gap-6 items-center justify-between">
          <div className="w-full md:w-1/2 relative">
            <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input 
              type="text" 
              placeholder="Buscar por título o descripción..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#050505] border border-white/10 text-white pl-12 pr-4 py-4 focus:outline-none focus:border-[#C5A059] transition-colors placeholder-gray-600 font-light"
            />
          </div>
          <div className="text-gray-500 text-xs tracking-widest uppercase">
            Mostrando {publicacionesFiltradas.length} ediciones
          </div>
        </div>

        {/* Grilla de Revistas */}
        {publicacionesFiltradas.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-10">
            {publicacionesFiltradas.map((pub) => (
              <article key={pub.id} className="group flex flex-col bg-[#0a0a0a] border border-white/5 hover:border-[#C5A059]/40 transition-all duration-500 shadow-xl overflow-hidden hover:-translate-y-2">
                
                {/* Portada de la Revista */}
                <div className="w-full aspect-[3/4] bg-gray-900 relative overflow-hidden flex items-center justify-center text-gray-600">
                  {pub.caratula_url ? (
                     <img 
                       src={pub.caratula_url} 
                       alt={pub.titulo} 
                       className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                     />
                  ) : (
                     <span className="text-xs uppercase tracking-widest">Sin Portada</span>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-80"></div>
                  
                  {/* Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="bg-[#C5A059] text-[#050505] text-[10px] tracking-[0.2em] uppercase px-3 py-1 font-bold shadow-md">
                      Oficial
                    </span>
                  </div>
                </div>

                {/* Detalles y Botones */}
                <div className="p-6 flex flex-col flex-grow">
                  <span className="text-[#C5A059] text-[10px] tracking-[0.2em] uppercase mb-2 block font-mono">
                    {formatearMes(pub.fecha_creacion)}
                  </span>
                  <h3 className="font-serif text-xl text-white mb-3 group-hover:text-[#C5A059] transition-colors">
                    {pub.titulo}
                  </h3>
                  <p className="text-gray-400 font-light text-sm leading-relaxed flex-grow mb-6 line-clamp-3">
                    {pub.descripcion_corta}
                  </p>
                  
                  <a 
                    href={pub.pdf_url} 
                    download 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full border border-white/20 text-center py-3 text-xs tracking-widest uppercase text-gray-300 hover:bg-[#C5A059] hover:text-[#050505] hover:border-[#C5A059] transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Ver Documento
                  </a>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-24 bg-[#0a0a0a] border border-white/5 shadow-lg">
            <svg className="w-12 h-12 text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <p className="text-gray-400 text-lg font-light">No se encontraron publicaciones con esos parámetros.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default Publicaciones;